import { Link } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TEMPLATES, CATEGORIES, PACKS, PACK_PRICE, getPopular, getPackTemplates } from '@/data/templates';
import { useAuth } from '@/contexts/AuthContext';
import TemplateCard from '@/components/TemplateCard';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [query, setQuery] = useState('');
  const [buying, setBuying] = useState('');
  const { user, login } = useAuth();
  const ownedPacks = user?.owned_packs || [];
  const isPro = !!user?.is_pro;
  const popular = getPopular();

  const buyPack = async (slug) => {
    if (!user) { login('/'); return; }
    setBuying(slug);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: slug,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
      setBuying('');
    }
  };

  const filtered = query.trim()
    ? TEMPLATES.filter(t =>
        t.title.toLowerCase().includes(query.toLowerCase()) ||
        t.description.toLowerCase().includes(query.toLowerCase()) ||
        t.category.toLowerCase().includes(query.toLowerCase())
      )
    : null;

  return (
    <div>
      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 -left-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-16 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-500 mb-5">
              <span className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
              <span>Everything PDF · Templates · Tools · AI</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Every form & doc
              <span className="block">you'll ever need —</span>
              <span className="text-orange-600 italic font-serif font-medium"> in one PDF.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl">
              NDAs, leases, invoices, resumes, offer letters, certificates… fill them in your browser,
              let AI draft the boring bits, and download a clean PDF in seconds.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Icons.Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search templates… NDA, invoice, resume"
                  className="w-full bg-white border border-stone-200 rounded-full pl-11 pr-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent shadow-sm"
                  data-testid="hero-search"
                />
              </div>
              <Link
                to="/tools"
                data-testid="hero-tools-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-stone-900 text-white text-sm font-medium btn-clay"
              >
                <Icons.Wrench size={16} /> PDF Tools
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-stone-500">
              <span className="flex items-center gap-1.5"><Icons.Check size={14} className="text-emerald-600" /> No login required</span>
              <span className="flex items-center gap-1.5"><Icons.Check size={14} className="text-emerald-600" /> Live preview</span>
              <span className="flex items-center gap-1.5"><Icons.Check size={14} className="text-emerald-600" /> AI auto-drafts</span>
              <span className="flex items-center gap-1.5"><Icons.Check size={14} className="text-emerald-600" /> Instant download</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200 shadow-xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/b270de8e-8327-425e-8e4f-3db98e8ebd13/images/c6edc2f0097abb17b54ecb14c1f44091ec3fa7bbd32b605ba8534eced2be04eb.png"
                alt="Paper layers"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 w-56 border border-stone-100 hidden sm:block">
              <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Just signed</div>
              <div className="text-sm font-display font-bold tracking-tight">Mutual NDA</div>
              <div className="text-xs text-stone-500 mt-0.5">Acme × Jane Doe</div>
            </div>
            <div className="absolute -top-3 -right-3 bg-stone-900 text-white rounded-2xl shadow-lg px-4 py-3 hidden sm:flex items-center gap-2">
              <Icons.Sparkles size={14} className="text-orange-400" />
              <span className="text-xs font-medium">AI · Draft for me</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── STARTER PACKS ─── */}
      {!filtered && (
        <section className="max-w-7xl mx-auto px-6 py-12" data-testid="packs-section">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">Bundles</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Starter packs</h2>
            <p className="text-stone-600 mt-2 text-sm sm:text-base max-w-xl">
              Grabbing everything for a big moment? These curated bundles put the right documents one click apart.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {PACKS.map((pack, i) => {
              const Icon = Icons[pack.icon] || Icons.Package;
              const items = getPackTemplates(pack.slug);
              return (
                <motion.div
                  key={pack.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="bg-white border border-stone-200/80 rounded-2xl p-6 flex flex-col"
                  data-testid={`pack-card-${pack.slug}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-11 h-11 rounded-xl grid place-items-center ${pack.tint}`}>
                      <Icon size={20} strokeWidth={1.75} />
                    </div>
                    <div>
                      <div className="font-display font-bold text-lg tracking-tight leading-tight">{pack.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-stone-400">{items.length} documents</div>
                    </div>
                  </div>
                  <p className="text-sm text-stone-500 leading-relaxed mb-4">{pack.tagline}</p>
                  <div className="flex flex-col gap-1.5">
                    {items.map((t) => {
                      const TIcon = Icons[t.icon] || Icons.FileText;
                      return (
                        <Link
                          key={t.id}
                          to={`/editor/${t.id}`}
                          data-testid={`pack-item-${t.id}`}
                          className="group flex items-center gap-2.5 px-3 py-2 rounded-xl border border-stone-200/70 hover:border-orange-300 hover:bg-orange-50/40 transition-all"
                        >
                          <TIcon size={15} strokeWidth={1.75} className="text-stone-500 group-hover:text-orange-600 transition-colors shrink-0" />
                          <span className="text-sm font-medium text-stone-800 truncate">{t.title}</span>
                          <Icons.ArrowRight size={13} className="ml-auto text-stone-300 group-hover:text-orange-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                        </Link>
                      );
                    })}
                  </div>
                  <div className="mt-auto pt-5 border-t border-stone-200/70 flex items-center justify-between gap-3">
                    {isPro ? (
                      <span className="text-sm text-stone-500 flex items-center gap-1.5" data-testid={`pack-pro-${pack.slug}`}>
                        <Icons.Crown size={14} className="text-orange-500" /> Included with Pro
                      </span>
                    ) : ownedPacks.includes(pack.slug) ? (
                      <span className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5" data-testid={`pack-owned-${pack.slug}`}>
                        <Icons.CheckCircle2 size={16} /> Unlocked — download clean
                      </span>
                    ) : (
                      <>
                        <div className="leading-none">
                          <span className="font-display font-black text-2xl tracking-tight">${PACK_PRICE}</span>
                          <span className="text-xs text-stone-400 ml-1">one-time</span>
                        </div>
                        <button
                          onClick={() => buyPack(pack.slug)}
                          disabled={buying === pack.slug}
                          data-testid={`pack-buy-${pack.slug}`}
                          className="inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white rounded-full px-4 py-2 text-sm font-medium btn-clay disabled:opacity-60"
                        >
                          {buying === pack.slug ? <Icons.Loader2 size={14} className="animate-spin" /> : <Icons.ShoppingBag size={14} />}
                          Buy pack
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* Search results */}
      {filtered && (
        <section className="max-w-7xl mx-auto px-6 py-6">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">
            {filtered.length} result{filtered.length === 1 ? '' : 's'} for "{query}"
          </h2>
          {filtered.length === 0 ? (
            <p className="text-stone-500 text-sm">Nothing matches — try a different word.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(t => <TemplateCard key={t.id} template={t} />)}
            </div>
          )}
        </section>
      )}

      {/* ─── CATEGORIES ─── */}
      {!filtered && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">Browse</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Pick a category</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.Folder;
              return (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                >
                  <Link
                    to={`/category/${c.slug}`}
                    data-testid={`category-${c.slug}`}
                    className="group block bg-white border border-stone-200/80 rounded-2xl p-5 hover:-translate-y-0.5 transition-all"
                  >
                    <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 ${c.tint}`}>
                      <Icon size={18} strokeWidth={1.75} />
                    </div>
                    <div className="font-display font-bold tracking-tight">{c.name}</div>
                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                      Explore <Icons.ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── POPULAR ─── */}
      {!filtered && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">Most used</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Popular templates</h2>
            </div>
            <Link to="#all" className="text-sm text-stone-700 hover:text-orange-600 hidden sm:flex items-center gap-1">
              See all <Icons.ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popular.map(t => <TemplateCard key={t.id} template={t} />)}
          </div>
        </section>
      )}

      {/* ─── PDF TOOLS PROMO ─── */}
      {!filtered && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="relative overflow-hidden rounded-3xl bg-stone-900 text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-orange-400 mb-4">
                <Icons.Wrench size={12} /> PDF Tools
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
                Merge, split & convert<br /> PDFs right in your browser.
              </h2>
              <p className="text-stone-300 mt-4 text-sm sm:text-base max-w-md">
                Combine multiple PDFs, split a file by pages, or turn images into a PDF — all client-side, no upload.
              </p>
              <Link
                to="/tools"
                data-testid="tools-promo-btn"
                className="inline-flex items-center gap-2 mt-6 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-5 py-3 text-sm font-medium btn-clay"
              >
                Open PDF Tools <Icons.ArrowRight size={14} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/b270de8e-8327-425e-8e4f-3db98e8ebd13/images/989915f7353418ce630f6a310623f98ddfbc065275f6515eeadf098dd948385d.png"
                alt="PDF tools"
                className="w-full h-auto rounded-2xl"
              />
            </div>
          </div>
        </section>
      )}

      {/* ─── ALL TEMPLATES ─── */}
      {!filtered && (
        <section id="all" className="max-w-7xl mx-auto px-6 py-12">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">Library</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">All templates</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TEMPLATES.map(t => <TemplateCard key={t.id} template={t} />)}
          </div>
        </section>
      )}
    </div>
  );
}
